import { Outlet, useLocation, useSearchParams } from "react-router-dom";
import { createContext, useEffect, useState, useRef, useCallback } from "react";
import { Admin, SubUser, User } from "type/type";
import { useSetRecoilState } from "recoil";
import { isDarkAtom } from "styles/atoms";
import { setHeaders, ACCESS_TOKEN_NAME, REFRESH_TOKEN_NAME, TYPE, authDecryptAes256, getAdminByAccToken, getUserByAccToken, decryptAES256, getSubUserByAccToken } from "api/api";
import { useModal } from "components/common/ModalLayout";
import { logout as agentLogout } from "api/bridge";
import Loading from "components/common/Loading";
import { logout as userLogout } from "api/bridgeUser";
import { useRecoilValue } from "recoil";
import { isFullScreenAtom, isSidebarAtom } from "styles/atoms";
import { rootFixedSize } from "styles/theme";
import Header from "./admin/Header";
import Footer from "./admin/Footer";
import styled from "styled-components";

const CommonMainContainer = styled.main`
  width: 100%;
  min-width: calc(1024px + 8rem);
  padding-left: 8rem;
  transition: all 0.4s ease-out;
  background-color: ${(prop) => prop.theme.mainBgColor};
  height: calc(100% - 3.5rem);
  &.show-sidebar {
    min-width: calc(1024px + 40.125rem);
    padding-left: 13.125rem;
  }
  &#mapContainer {
    position: relative;
    height: calc(100% - 3.5rem);
  }
  > section {
    height: 100%;
    min-width: 1024px;
  }
  .font-bold {
    font-family: var(--font-pretendard-bold);
  }
`;

export const FilterContentContainer = styled.nav`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem 1.5rem;

  .flex-row {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.5rem;
  }

  label {
    width: 3rem;
  }
  input, select {
    color: #121212;
    padding: ${rootFixedSize.inputShortPadding};
    border-radius: ${rootFixedSize.inputBorderRadius};
    &:focus-visible {
      outline-color: ${prop => prop.theme.borderAccent};
    }
  }
  
  select:hover {
    cursor: pointer;
  }
  
  svg {
    width: 1.5rem;
    height: 1.5rem;
    &:hover {
      cursor: pointer;
      opacity: 0.7;
    }
  }

  .btn-wrap {
    margin-left: auto;
    white-space: nowrap;
  }
`;

export const CommonTableWrap = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow: auto;
}`;

export const CommonTable = styled.table`
  border-collapse: collapse;
  border: 0;
  th, td {
    min-width: 4rem;
    padding: 0.6rem;
    text-align: center;
  }
  thead {
    font-weight: 400;
    font-family: var(--font-pretendard-bold);
    color: ${prop => prop.theme.tableHeadTextColor};
    position: relative;
    z-index: 4;
  }
  th {
    position: sticky;
    top: 0;
    background-color: ${prop => prop.theme.tableHeadBgColor};
  }
  tbody {
    tr {
      &:hover {
        background-color: ${props => props.theme.tableBodyTrHoverBgColor};
      }
    }
    td {
      border: 1px solid ${prop => prop.theme.tableBorderColor};
      color: ${prop => prop.theme.tableBodyTextColor};
      &:first-child {
        border-left: 0;
      }
      &:last-child {
        border-right: 0;
      }
    }
    &:hover {
        cursor: pointer;
    }
  }

  .state {
    span {
      padding: 0.125rem 0.5rem;
      border-radius: 0.25rem;
    }

    &[data-status='state_0'] span {
      background-color: #a9a9a9; //대기, 회색
      color: #fff;
    }
    &[data-status='state_1'] span {
      background-color: #1568dd; //진행중, 파란색
      color: #fff;
    }
    &[data-status='state_2'] span {
      background-color: #fdd032; //접수완료, 노란색
      color: #262626;
    }
    &[data-status='state_3'] span {
      background-color: #b700ff; //처리완료, 보라색
      color: #fff;
    }
    &[data-status='state_4'] span {
      background-color: #2faf5b; //답변완료, 초록색
      color: #fff;
    }
    &[data-status='state_5'] span {
      background-color: #e33f3f; //취소완료, 빨간색
      color: #fff;
    }
  }

  .ellipsis-text {
    white-space: nowrap;      /* 텍스트가 한 줄로 표시되도록 설정 */
    overflow: hidden;         /* 넘치는 텍스트를 숨김 */
    text-overflow: ellipsis;  /* 넘친 텍스트를 '...'로 표시 */
    max-width: 200px;
  }
  .w-5 {
    width: 5%;
  }
  .w-10 {
    width: 10%;
  }
  .w-15 {
    width: 15%;
  }
  .w-20 {
    width: 20%;
  }
  .w-30 {
    width: 20%;
  }
`;


type AdminLoginInfoContextType = {
  adminLoginInfo: Admin;
  setAdminLoginInfo: React.Dispatch<React.SetStateAction<Admin>>;
};
type AgentLoginInfoContextType = {
  agentLoginInfo: Admin;
  setAgentLoginInfo: React.Dispatch<React.SetStateAction<Admin>>;
};
type UserLoginInfoContextType = {
  userLoginInfo: User|null;
  setUserLoginInfo: React.Dispatch<React.SetStateAction<User>>;
};

type SubUserLoginInfoContextType = {
  subUserLoginInfo: SubUser|null;
  setSubUserLoginInfo: React.Dispatch<React.SetStateAction<SubUser>>;
};

export const AgentLoginInfoContext = createContext<AgentLoginInfoContextType>({ agentLoginInfo: {} as Admin, setAgentLoginInfo: () => { } });
export const UserLoginInfoContext = createContext<UserLoginInfoContextType>({ userLoginInfo: null, setUserLoginInfo: () => {} });
export const SubUserLoginInfoContext = createContext<SubUserLoginInfoContextType>({ subUserLoginInfo: null, setSubUserLoginInfo: () => {} });
export const AdminLoginInfoContext = createContext<AdminLoginInfoContextType>({ adminLoginInfo: {} as Admin, setAdminLoginInfo: () => {}});

function AdminLayout() {
  const elementRef = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();
  const isSidebar = useRecoilValue(isSidebarAtom);
  const isFullScreen = useRecoilValue(isFullScreenAtom);
  const [isCertified, setIsCertified] = useState(true);
  const [adminLoginInfo, setAdminLoginInfo] = useState<Admin>({} as Admin);
  const type = localStorage.getItem(TYPE);
  const accessToken = localStorage.getItem(ACCESS_TOKEN_NAME);
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_NAME);

  // 관리자 정보 조회 & 인증 체크
  const mainApi = async () => {
    if (!type || !accessToken || !refreshToken) {
      alert('인증되지 않은 사용자입니다.');
      setIsCertified(false);
      return;
    }

    const response = await getAdminByAccToken('Admin');
    if (response.code === 200) {
      const { REACT_APP_SECRET_KEY, REACT_APP_SECRET_IV } = process.env;
      setAdminLoginInfo({
        ...response.data,
        mobile: decryptAES256(response.data.mobile, REACT_APP_SECRET_KEY!, REACT_APP_SECRET_IV!),
      });
    } else {
      setIsCertified(false);
    }
  };

  // 인증 정보 검증
  useEffect(() => {
    const isHttp = window.location.protocol === 'http:';
    const isHappyCallSend = pathname === '/admin/happy-call/send';
  
    if (isHttp && !isHappyCallSend && !window.location.port) {
      const httpsUrl = `https://${window.location.hostname}${window.location.pathname}${window.location.search}`;
      window.location.replace(httpsUrl);
      return;
    }
  
    // 1. 인증 정보 없을 때 - auth 쿼리 파라미터 복호화
    if ((!type || !accessToken || !refreshToken) && isHttp && isHappyCallSend) {
      const searchParams = new URLSearchParams(window.location.search);
      const aesParameter = searchParams.get('auth') || "";

      if (aesParameter) {
        const { REACT_APP_SECRET_KEY, REACT_APP_SECRET_IV } = process.env!;
        if (!REACT_APP_SECRET_KEY || !REACT_APP_SECRET_IV) return;

        const decryptedAuth = decryptAES256(
          decodeURIComponent(aesParameter),
          REACT_APP_SECRET_KEY,
          REACT_APP_SECRET_IV
        );
        const authArr = decryptedAuth.split("||");
        setHeaders(authArr[0], authArr[1], authArr[2]);

        // 인증 저장 후 http 리로드
        const httpUrl = `http://${window.location.hostname}${window.location.pathname}`;
        window.location.replace(httpUrl);
        return;
      }
    }

    // 2. 이미 인증된 경우: URL에서 쿼리 제거만 (리로드 없이)
    if (type && accessToken && refreshToken && isHttp && isHappyCallSend && window.location.search) {
      const cleanUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
      window.history.replaceState(null, '', cleanUrl);
    }

    mainApi();
  }, [pathname]);
  // 전체 화면 모드 처리
  useEffect(() => {
    const element = elementRef.current;

    if (isFullScreen) {
      element?.requestFullscreen?.();
    } else if (document.fullscreenElement) {
      document.exitFullscreen();
    }
  }, [isFullScreen]);
  // 인증 실패 시 로그인 페이지로 이동
  useEffect(() => {
    if (!isCertified) {
      window.location.href = "/admin/login";
    }
  }, [isCertified]);
  const activeMapPaths = ['/mapboardGoogle', '/mapboardImage', '/mapboardKakao'];
  return (
    <AdminLoginInfoContext.Provider value={{ adminLoginInfo, setAdminLoginInfo }}>
      <div ref={elementRef} style={{ height: "100%" }}>
        <Header />
          <CommonMainContainer id={activeMapPaths.includes(pathname) ? "mapContainer" : "page"} className={isSidebar ? 'show-sidebar' : ''}>
            <Outlet />
          </CommonMainContainer>
        <Footer />
      </div>
    </AdminLoginInfoContext.Provider>
  );
}

function AgentLayout() {
  const location = useLocation();
  const pathName = location.pathname;
  const { showModal } = useModal();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [agentLoginInfo, setAgentLoginInfo] = useState<Admin>({} as Admin);
  const [isAcctoken, setIsAcctoken] = useState<boolean>(true);
  const [isTimestamp, setIsTimestamp] = useState<boolean>(false);
  const setDarkAtom = useSetRecoilState(isDarkAtom);

  useEffect(() => {
    setDarkAtom(false);
  }, [setDarkAtom]);

  const uncertifiedToken = useCallback(() => {
    setIsAcctoken(false);
    showModal("alert", "인증되지 않은 사용자입니다.", { confirm: true, cancel: true }, () => {
      agentLogout();
    });
  }, []);
  
  useEffect(() => {
    const validateTimestamp = (timestamp: string | number) => {
      const currentTimestamp = Math.floor(Date.now() / 1000); // 현재 타임스탬프 (초 단위)
      if (Math.abs(currentTimestamp - Number(timestamp)) > 10) {
        uncertifiedToken();
      }
      setIsTimestamp(true);
    };
  
    const processAuth = (auth: string) => {
      const decryptRes = authDecryptAes256(auth.replaceAll(" ", "+"));
      if (decryptRes.code === 200) {
        const [accessToken, refreshToken, timestamp] = decryptRes.data || [];
        if (!isTimestamp) validateTimestamp(timestamp);
        setHeaders(accessToken, refreshToken, "Agent");
      }
    };
  
    const processLocalStorageTokens = async () => {
      const type = localStorage.getItem(TYPE);
      const accessToken = localStorage.getItem(ACCESS_TOKEN_NAME);
      const refreshToken = localStorage.getItem(REFRESH_TOKEN_NAME);
      if (!type || !accessToken || !refreshToken) {
        return false;
      }
  
      const response = await getAdminByAccToken("Agent");
      if (response.code === 200) {
        const { REACT_APP_SECRET_KEY, REACT_APP_SECRET_IV } = process.env;
        setAgentLoginInfo({
          ...response.data,
          mobile: decryptAES256(response.data.mobile, REACT_APP_SECRET_KEY!, REACT_APP_SECRET_IV!),
        });
      }
      return response.code === 200;
    };
  
    const handleAuth = async () => {
      setLoading(true);
      try {
        const auth = searchParams.get("auth");
      
        // 인증 처리: auth가 있으면 processAuth 실행
        if (auth) processAuth(auth);
      
        // LocalStorage 토큰 처리 (항상 호출)
        const isLocalStorageValid = await processLocalStorageTokens();
      
        // 최종 인증 여부 확인
        if (!isLocalStorageValid) {
          uncertifiedToken();
        }
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };
  
    handleAuth();
  }, [searchParams, pathName, isTimestamp, uncertifiedToken]);

  // 인증 정보 검증
  useEffect(() => {
    if (window.location.protocol === 'http:' && !window.location.port) {
      const httpsUrl = `https://${window.location.hostname}${window.location.pathname}${window.location.search}`;
      window.location.replace(httpsUrl);
    }
  }, []);
  return (
    <>
      {loading ? <Loading />
      :
      isAcctoken &&
        <AgentLoginInfoContext.Provider value={{ agentLoginInfo, setAgentLoginInfo }}>
          <Outlet />
        </AgentLoginInfoContext.Provider>
      }
    </>
  );
}

function UserLayout() {
  const location = useLocation();
  const pathName = location.pathname;
  const { showModal } = useModal();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [userLoginInfo, setUserLoginInfo] = useState<User>({} as User);
  const [subUserLoginInfo, setSubUserLoginInfo] = useState<SubUser>({} as SubUser);
  const [isAcctoken, setIsAcctoken] = useState<boolean>(true);
  const [isMasterUser, setIsMasterUser] = useState<boolean>(true);
  const setDarkAtom = useSetRecoilState(isDarkAtom);

  const uncertifiedToken = useCallback(() => {
    setIsAcctoken(false);
    showModal("alert", "인증되지 않은 사용자입니다.", { confirm: true, cancel: true }, () => {
      userLogout();
    });
  }, []);
  
  useEffect(() => {
    // TODO 타임스탬프 보안이 필요하다면 살리기
    // const validateTimestamp = (timestamp: string | number) => {
    //   const currentTimestamp = Math.floor(Date.now() / 1000); // 현재 타임스탬프 (초 단위)
    //   if (Math.abs(currentTimestamp - Number(timestamp)) > 86400) {
    //     uncertifiedToken();
    //   }
    // };
  
    const processAuth = async (auth: string) => {
      const decryptRes = authDecryptAes256(auth.replaceAll(" ", "+"));
      // console.log('decryptRes', decryptRes);
      if (decryptRes.code === 200) {
        const [accessToken, refreshToken, timestamp] = decryptRes.data || [];
        // TODO 고객앱은 웹뷰 호출이 잦으므로, 추후 타임스탬프 로직 새로 개발 필요
        // validateTimestamp(timestamp);
        setHeaders(accessToken, refreshToken, "User");
      }
    };
  
    const processLocalStorageTokens = async () => {
      const type = localStorage.getItem(TYPE);
      const accessToken = localStorage.getItem(ACCESS_TOKEN_NAME);
      const refreshToken = localStorage.getItem(REFRESH_TOKEN_NAME);
  
      if (type !== "User" || !accessToken || !refreshToken) {
        return false;
      }
  
      let response = await getUserByAccToken();
      if (response.code === 200) {
        const { REACT_APP_SECRET_KEY, REACT_APP_SECRET_IV } = process.env;
        setUserLoginInfo({
          ...response.data,
          mobile: decryptAES256(response.data.mobile, REACT_APP_SECRET_KEY!, REACT_APP_SECRET_IV!),
          password: '',
        });
      } else {
        setIsMasterUser(false);
        response = await getSubUserByAccToken();
        if (response.code === 200) {
          setSubUserLoginInfo(response.data);
        }
      }
      return response.code === 200;
    };
  
    const handleAuth = async () => {
      setLoading(true);
      const auth = searchParams.get("auth");
      
      // 인증 처리: auth가 있으면 processAuth 실행
      if (auth) processAuth(auth);
    
      // LocalStorage 토큰 처리 (항상 호출)
      const isLocalStorageValid = await processLocalStorageTokens();
  
      if (!isLocalStorageValid) {
        uncertifiedToken();
      }
      setLoading(false);
    };
  
    handleAuth();

    // 테스트용
    // uncertifiedToken();
  }, [searchParams, pathName, uncertifiedToken]);
  useEffect(() => {
    setDarkAtom(false);
  }, [setDarkAtom]);
  return (
    <>
      {loading ? <Loading />
      :
      (isAcctoken && isMasterUser) ?
        <UserLoginInfoContext.Provider value={{ userLoginInfo, setUserLoginInfo }}>
          <Outlet />
        </UserLoginInfoContext.Provider>
      :
      (isAcctoken && !isMasterUser) &&
        <SubUserLoginInfoContext.Provider value={{ subUserLoginInfo, setSubUserLoginInfo }}>
          <Outlet />
        </SubUserLoginInfoContext.Provider>
      }
    </>
    
  );
}

function UserTheme() {
  const setDarkAtom = useSetRecoilState(isDarkAtom);

  useEffect(() => {
    setDarkAtom(false);
  }, [setDarkAtom]);

  return (
    <>
      <Outlet />
    </>
  );
}

export { AdminLayout, AgentLayout, UserLayout, UserTheme };